import Phaser from "phaser";
import { TILE } from "@/config";

/**
 * Logical tile atlas: the game refers to tiles by name ("grass", "path_tl", "tree_a_bl"…), and at
 * runtime the named tiles are copied from the Ninja Adventure tilesets into one canvas texture
 * called "tiles". Coordinates below are (column, row) in the source tileset, in 16 px tiles.
 * Swapping art later only means changing this table.
 */

type SourceKey =
  | "tileset-field"
  | "tileset-nature"
  | "tileset-house"
  | "tileset-water"
  | "tileset-camp"
  | "tileset-element"
  | "tileset-desert";

interface TileSource {
  key: SourceKey;
  col: number;
  row: number;
}

const field = (col: number, row: number): TileSource => ({ key: "tileset-field", col, row });
const nature = (col: number, row: number): TileSource => ({ key: "tileset-nature", col, row });
const house = (col: number, row: number): TileSource => ({ key: "tileset-house", col, row });
const water = (col: number, row: number): TileSource => ({ key: "tileset-water", col, row });
const camp = (col: number, row: number): TileSource => ({ key: "tileset-camp", col, row });
const element = (col: number, row: number): TileSource => ({ key: "tileset-element", col, row });
const desert = (col: number, row: number): TileSource => ({ key: "tileset-desert", col, row });

const DEFS: Record<string, TileSource> = {
  // ground
  grass: field(1, 4),
  grass_b: field(3, 3),
  grass_c: field(4, 4),
  grass_d: field(3, 4),

  // dirt (3×3 autotile block + 2×2 inner corners)
  path: field(1, 1),
  path_tl: field(0, 0),
  path_t: field(1, 0),
  path_tr: field(2, 0),
  path_l: field(0, 1),
  path_r: field(2, 1),
  path_bl: field(0, 2),
  path_b: field(1, 2),
  path_br: field(2, 2),
  path_itl: field(3, 0),
  path_itr: field(4, 0),
  path_ibl: field(3, 1),
  path_ibr: field(4, 1),

  // darker grass patches (same layout, rows 6–8)
  dark: field(1, 7),
  dark_tl: field(0, 6),
  dark_t: field(1, 6),
  dark_tr: field(2, 6),
  dark_l: field(0, 7),
  dark_r: field(2, 7),
  dark_bl: field(0, 8),
  dark_b: field(1, 8),
  dark_br: field(2, 8),
  dark_itl: field(3, 6),
  dark_itr: field(4, 6),
  dark_ibl: field(3, 7),
  dark_ibr: field(4, 7),

  // pond / river on grass (3×3 autotile block)
  water: water(1, 7),
  water_tl: water(0, 6),
  water_t: water(1, 6),
  water_tr: water(2, 6),
  water_l: water(0, 7),
  water_r: water(2, 7),
  water_bl: water(0, 8),
  water_b: water(1, 8),
  water_br: water(2, 8),

  // sandy beach (3×3 autotile block + inner corners, field rows 9–11)
  sand: field(1, 10),
  sand_tl: field(0, 9),
  sand_t: field(1, 9),
  sand_tr: field(2, 9),
  sand_l: field(0, 10),
  sand_r: field(2, 10),
  sand_bl: field(0, 11),
  sand_b: field(1, 11),
  sand_br: field(2, 11),
  sand_itl: field(3, 9),
  sand_itr: field(4, 9),
  sand_ibl: field(3, 10),
  sand_ibr: field(4, 10),

  // the sea: water with a sandy rim (top-left block of the water tileset); collides like water
  watersand: water(1, 1),
  watersand_tl: water(0, 0),
  watersand_t: water(1, 0),
  watersand_tr: water(2, 0),
  watersand_l: water(0, 1),
  watersand_r: water(2, 1),
  watersand_bl: water(0, 2),
  watersand_b: water(1, 2),
  watersand_br: water(2, 2),

  // wooden bridge over the river (planks with railings)
  bridge_top: water(1, 12),
  bridge_mid: water(1, 13),
  bridge_bottom: water(1, 14),

  // village props
  campfire_tl: camp(10, 3),
  campfire_tr: camp(11, 3),
  campfire_bl: camp(10, 4),
  campfire_br: camp(11, 4),
  log_top: camp(0, 1),
  log_bottom: camp(0, 2),
  hay: camp(0, 0),
  hay_round: camp(2, 0),
  signpost: element(0, 2),
  stump_tl: nature(0, 8),
  stump_tr: nature(1, 8),
  stump_bl: nature(0, 9),
  stump_br: nature(1, 9),
  rock_big_tl: nature(13, 8),
  rock_big_tr: nature(14, 8),
  rock_big_bl: nature(13, 9),
  rock_big_br: nature(14, 9),
  rock_grey_tl: nature(16, 8),
  rock_grey_tr: nature(17, 8),
  rock_grey_bl: nature(16, 9),
  rock_grey_br: nature(17, 9),
  rock_small: nature(18, 9),
  rock_small_orange: nature(15, 9),

  // small nature objects
  bush: nature(10, 8),
  bush_orange: nature(9, 8),
  tuft_a: nature(2, 10),
  tuft_b: nature(6, 10),
  flower_sun: nature(0, 11),
  flower_yellow: nature(1, 11),
  flower_red: nature(3, 11),
  flower_white: nature(6, 11),
  flower_orange: nature(0, 14),
  flower_blue: nature(1, 14),
  flower_pink: nature(2, 14),

  // forest floor and ruins decor
  mushroom_a: nature(4, 17),
  mushroom_b: nature(5, 17),
  mushroom_c: nature(6, 17),
  bones_a: desert(16, 11),
  bones_b: desert(17, 11),
};

// prefab houses: 4 tiles wide × 3 tall, top-left of each in TilesetHouse
const HOUSES: Record<string, [number, number]> = {
  house_a: [0, 0],
  house_b: [4, 0],
  house_c: [8, 0],
  house_d: [12, 0],
};
export const HOUSE_W = 4;
export const HOUSE_H = 3;
for (const [name, [c0, r0]] of Object.entries(HOUSES)) {
  for (let r = 0; r < HOUSE_H; r++)
    for (let c = 0; c < HOUSE_W; c++) DEFS[`${name}_${r}_${c}`] = house(c0 + c, r0 + r);
}

export const TILE_IDS: readonly string[] = Object.keys(DEFS);

const INDEX: Record<string, number> = {};
TILE_IDS.forEach((id, i) => (INDEX[id] = i));

/** Index of a named tile inside the composed "tiles" texture. Throws on unknown names. */
export function tid(id: string): number {
  const i = INDEX[id];
  if (i === undefined) throw new Error(`Unknown tile id: ${id}`);
  return i;
}

export const WATER_TILE_IDS: number[] = TILE_IDS.filter((id) => id.startsWith("water")).map(tid);
export const PATH_TILE_IDS: number[] = TILE_IDS.filter((id) => id.startsWith("path")).map(tid);

const ATLAS_COLUMNS = 16;

/** Compose the "tiles" canvas texture from the loaded tileset images. Safe to call more than once. */
export function buildTileAtlas(scene: Phaser.Scene): void {
  if (scene.textures.exists("tiles")) return;
  const rows = Math.ceil(TILE_IDS.length / ATLAS_COLUMNS);
  const canvas = scene.textures.createCanvas("tiles", ATLAS_COLUMNS * TILE, rows * TILE);
  if (!canvas) throw new Error("could not create tiles canvas");
  const ctx = canvas.getContext();
  const sources = new Map<SourceKey, CanvasImageSource>();
  const source = (key: SourceKey): CanvasImageSource => {
    let img = sources.get(key);
    if (!img) {
      img = scene.textures.get(key).getSourceImage() as CanvasImageSource;
      sources.set(key, img);
    }
    return img;
  };
  TILE_IDS.forEach((id, i) => {
    const d = DEFS[id] as TileSource;
    const dx = (i % ATLAS_COLUMNS) * TILE;
    const dy = Math.floor(i / ATLAS_COLUMNS) * TILE;
    ctx.drawImage(source(d.key), d.col * TILE, d.row * TILE, TILE, TILE, dx, dy, TILE, TILE);
  });
  canvas.refresh();
}
